import { useSelector } from "react-redux";
import ConversationItem from "./ConversationItem";
import { memo, useCallback, useMemo, useState, useEffect, useRef } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Button, Input, List, Result, Space } from "antd";
import { useNavigate, useOutletContext, useParams } from "react-router";
import {
  agentsSelector,
  conversationSelector,
  conversationTypeSelector,
  crmIsoSettingSelector, // [crm-iso]
  isActiveISOSettingSelector, // [crm-iso]
  isAssignModeSelector,
  loadedInitSelector,
  pusherConnectedSelector,
  tagsSelector,
  websiteIdsSelector,
} from "appredux/selectors";
import { isAffectedProviderConversation } from "components/apps/settings/CrmIsoSetting/helper"; // [crm-iso]
import { useQueryClient } from "@tanstack/react-query";
import { useOpenConversation } from "queries/conversation";
import { useTranslation } from "react-i18next";
import {
  ONLY_FOR_INTERNAL,
  conversationStatuses,
  messageTypes,
} from "utils/static";
import WaveSpinnerLoader from "components/UI/customLoading";
import InfiniteScroll from "react-infinite-scroller";
import { includes, isEmpty, last } from "lodash-es";
import dayjs from "dayjs";
import { autoTransferWaitingClient } from "apis/conversation";

const REASSIGN_AFTER_MIN = 10;
const COUNTDOWN_SHOW_AFTER_MIN = 5;
// Toleransi telat tembakan countdown. Tembakan live yang sehat telatnya
// ≤ ~1 detik (interval 1s) + jitter; lebih dari ini berarti timer sempat mati
// (tab sleep/throttle/Pusher reconnect) sehingga cache bisa basi.
const EXPIRED_FIRE_GRACE_MS = 3000;

const ITEMS_PER_PAGE = 10;

const ConversationList = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const currentConversation = useSelector(conversationSelector);
  const tags = useSelector(tagsSelector);
  const loadedInit = useSelector(loadedInitSelector);
  const {
    conversations: _conversations,
    isLoading,
    isFetching,
  } = useOutletContext();
  const websiteId = useSelector(websiteIdsSelector);

  const conversation_type = useSelector(conversationTypeSelector);
  const isAssignMode = useSelector(isAssignModeSelector);
  const agents = useSelector(agentsSelector);
  const conversationType = useSelector(conversationTypeSelector);
  const pusherConnected = useSelector(pusherConnectedSelector);
  const [page, setPage] = useState(1);
  const containerRef = useRef(null);

  useEffect(() => {
    setPage(1);
  }, [id]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [id]);

  // Di route /conversations/:id/list selalu tampilkan hanya conversation OPEN
  // (filter yang CLOSE), apa pun conversation_type-nya. Route /search dirender
  // oleh komponen terpisah (ConversationsFilter) sehingga tidak terpengaruh.
  // Pakai `!== CLOSE` (bukan `=== OPEN`) supaya status_id yang kosong/undefined
  // tetap dianggap OPEN, bukan ikut tersembunyi.
  // [crm-iso] START — hidden feature (lihat CrmIsoSetting/TEARDOWN.md).
  // Bila fitur CRM ISO aktif untuk agen ini, daftar percakapan dibatasi hanya ke
  // nomor kontak yang provider-nya di-setting (affected_providers).
  // TEARDOWN: kembalikan openConversations ke bentuk semula (hanya filter CLOSE).
  const crmIsoSetting = useSelector(crmIsoSettingSelector);
  const isActiveISO = useSelector(isActiveISOSettingSelector);
  // [crm-iso] END

  const openConversations = useMemo(() => {
    // Selalu sembunyikan percakapan yang CLOSE (apa pun conversation_type-nya).
    const base = _conversations.filter(
      (conversation) => conversation.status_id !== conversationStatuses.CLOSE,
    );

    // [crm-iso] START
    if (!isActiveISO) return base;

    return base.filter((conversation) =>
      isAffectedProviderConversation(
        conversation,
        crmIsoSetting?.affected_providers,
      ),
    );
    // [crm-iso] END
  }, [_conversations, isActiveISO, crmIsoSetting]); // [crm-iso] deps: isActiveISO, crmIsoSetting

  const conversations = useMemo(() => {
    const filteredConversations = openConversations.map((conversation) => {
      const filteredMessages = conversation.messages.filter(
        (message) => !messageTypes.SYSTEM.includes(message.type_id),
      );
      const lastMessage = last(filteredMessages);
      const lastRawMessage = last(conversation.messages);
      let counter = null;
      const lastMessageMs = lastMessage?.message_time
        ? dayjs(lastMessage.message_time).valueOf()
        : null;

      const isStaleNonManual =
        !conversation.is_manual_transfer &&
        Number.isFinite(lastMessageMs) &&
        Date.now() - lastMessageMs > REASSIGN_AFTER_MIN * 60 * 1000;
      const isTransferLast = lastRawMessage?.type_id === 70;

      if (
        conversation_type === "my_inbox" &&
        lastMessage?.is_out === 1 &&
        ONLY_FOR_INTERNAL.includes(websiteId)
      ) {
        let messageMs = dayjs(lastMessage.message_time).valueOf();

        if (isTransferLast || isStaleNonManual) {
          messageMs = dayjs(lastRawMessage?.message_time).valueOf();
        }

        if (Number.isFinite(messageMs)) {
          const reassignAt = messageMs + REASSIGN_AFTER_MIN * 60 * 1000;
          counter = {
            /** Batas waktu terlempar (FE mirror aturan BE: T + 10 menit) */
            reassignAt,
            /** Baru tampil counter setelah 5 menit berjalan (sisa menuju menit ke-10) */
            showAfter: messageMs + COUNTDOWN_SHOW_AFTER_MIN * 60 * 1000,
            /** Jam (HH:mm) saat percakapan akan auto-transfer */
            reassignAtLabel: dayjs(reassignAt).format("HH:mm"),
          };
        }
      }
      return { ...conversation, messages: filteredMessages, counter };
    });

    return filteredConversations.slice(0, page * ITEMS_PER_PAGE);
  }, [openConversations, page, conversation_type, websiteId]);

  const hasMore = useMemo(() => {
    return conversations.length < openConversations.length;
  }, [conversations.length, openConversations.length]);

  const queryKey = useMemo(() => {
    return !isAssignMode
      ? ["conversations", "list"]
      : ["conversations", "list", { inbox_filter: conversation_type }];
  }, [isAssignMode, conversation_type]);

  const createChatHandler = useCallback(() => {
    navigate(`/conversations/${id}/create`);
  }, [id, navigate]);

  const openConversation = useOpenConversation();
  const selectedHandler = useCallback(
    (item) => {
      // Buka conversation: tampil instan dari cache + muat history penuh
      // (getConversation sekali per conversation) supaya history tidak kosong.
      openConversation(item);
    },
    [openConversation],
  );

  const showFilterHandler = useCallback(() => {
    navigate(`/conversations/${id}/search?phone_number_id=${id}`);
  }, [id, navigate]);

  // Dibaca lewat ref oleh handleCountdownExpired: ConversationItem di-memo
  // dengan compareWithJSON (prop fungsi tidak ikut dibandingkan), jadi item bisa
  // memegang versi handler lama. Lewat ref, versi mana pun membaca nilai terbaru.
  const pusherConnectedRef = useRef(pusherConnected);
  pusherConnectedRef.current = pusherConnected;
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;

  // Status penanganan per countdown (`conversationId:reassignAt`). Disimpan di
  // level list supaya tidak hilang saat item ter-mount ulang (list diganti
  // spinner tiap refetch). Tiap countdown paling banyak 1x refetch & 1x
  // auto-transfer — tanpa ini, refetch → mount ulang → expired lagi → refetch
  // berulang tiap ~1 detik selama server belum memindahkan conversation.
  //   "syncing": sudah refetch untuk memastikan data server
  //   "done"   : auto-transfer sudah dipanggil
  const countdownStatusRef = useRef(new Map());

  // Saat countdown habis: hit API auto-transfer + hapus conversation dari
  // cache supaya hilang dari list seketika. Bila API gagal, BE punya cron
  // 10 menit yang tetap menjalankan transfer — jadi tidak perlu rollback.
  const handleCountdownExpired = useCallback(
    (conversationId, reassignAt) => {
      // Guard: jika koneksi Pusher sedang putus, kita mungkin melewatkan balasan
      // customer yang sebenarnya sudah masuk. Jangan auto-transfer/hapus dari FE;
      // biarkan cron BE (10 menit) yang otoritatif menangani bila memang stale.
      if (!pusherConnectedRef.current) return;

      const statusKey = `${conversationId}:${reassignAt}`;
      const status = countdownStatusRef.current.get(statusKey);
      if (status === "done") return;

      const currentQueryKey = queryKeyRef.current;
      // Guard: tembakan telat (timer sempat mati / item ter-render saat sudah
      // lewat) berarti jendela 10 menit lewat tanpa pengawasan. Bila list
      // terakhir diambil SEBELUM batas waktu, cache bisa basi (mis. balasan
      // customer terlewat) → refetch sekali dulu. Bila setelah itu conversation
      // masih ada, item expired lagi dan lanjut ke auto-transfer di bawah.
      // Bila list sudah diambil SETELAH batas waktu (mis. baru pindah tab),
      // datanya pasti segar → langsung auto-transfer.
      const isLate = Date.now() - reassignAt > EXPIRED_FIRE_GRACE_MS;
      const fetchedAt = queryClient.getQueryData(currentQueryKey)?.fetched_at;
      if (isLate && !(fetchedAt >= reassignAt)) {
        if (status === "syncing") return;
        countdownStatusRef.current.set(statusKey, "syncing");
        queryClient
          .invalidateQueries({
            queryKey: currentQueryKey,
            refetchType: "active",
          })
          .catch(() => {});
        return;
      }

      countdownStatusRef.current.set(statusKey, "done");
      autoTransferWaitingClient(conversationId).catch(() => {});
      queryClient.setQueryData(currentQueryKey, (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.filter((c) => c.id !== conversationId),
        };
      });
    },
    [queryClient],
  );

  const loadMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const renderItem = useCallback(
    (item, index) => {
      let filterTags = [];
      // [crm-iso] Agen terpengaruh tidak melihat tag apa pun di daftar percakapan.
      // TEARDOWN: hapus `!isActiveISO &&` di kondisi bawah + isActiveISO dari deps.
      if (!isActiveISO && !isEmpty(item?.contact?.tag_ids)) {
        filterTags = tags.filter((tag) =>
          includes(item.contact.tag_ids, tag.id),
        );
      }
      let agentName = "";
      if (isAssignMode && conversationType === "assigned") {
        agentName = agents.find(
          (agent) => agent.id === item.current_agent_id,
        )?.name;
      }
      return (
        <ConversationItem
          onSelect={() => selectedHandler(item)}
          key={item.id}
          item={item}
          isSelected={currentConversation?.id === item.id}
          index={index}
          showInfoNew
          tags={filterTags}
          assignedAgent={agentName}
          onCountdownExpired={handleCountdownExpired}
        />
      );
    },
    [
      currentConversation?.id,
      selectedHandler,
      tags,
      agents,
      isAssignMode,
      conversationType,
      handleCountdownExpired,
      isActiveISO, // [crm-iso]
    ],
  );

  return (
    <>
      <div className="pb-2 pt-4 px-3 bg-white">
        <Space.Compact className="mb-2 w-full">
          <Button
            style={{
              borderRight: "none",
              borderRadius: "6px 0 0 6px",
              cursor: "default",
            }}
            disabled
          >
            <SearchOutlined className="text-gray-500" />
          </Button>
          <Input
            placeholder="Search..."
            onFocus={showFilterHandler}
            className="border-gray-200"
            style={{
              borderLeft: "1px solid #d9d9d9",
              borderRadius: "0 6px 6px 0",
            }}
          />
        </Space.Compact>
      </div>
      <div
        ref={containerRef}
        className="bg-grey-lighter flex-1 overflow-y-auto"
      >
        {!loadedInit || isLoading || isFetching ? (
          // Spinner saat fetch (termasuk pindah tab) supaya tidak terlihat data
          // tab lama bercampur data tab baru.
          <WaveSpinnerLoader classNameProps="mt-10" />
        ) : isEmpty(conversations) ? (
          <div className="flex w-full h-full justify-center ">
            <Result
              status="404"
              title={t("no_chat_yet")}
              subTitle={t("please_start_chatting_first")}
              extra={
                <Button type="primary" onClick={createChatHandler}>
                  {t("start_chat")}
                </Button>
              }
            />
          </div>
        ) : (
          <InfiniteScroll
            pageStart={0}
            loadMore={loadMore}
            hasMore={hasMore}
            useWindow={false}
            loader={<WaveSpinnerLoader key={0} classNameProps="mt-4" />}
          >
            <List dataSource={conversations} renderItem={renderItem} />
          </InfiniteScroll>
        )}
      </div>
    </>
  );
};

export default memo(ConversationList);
